import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Fab,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  Edit,
  PlayArrow,
  Add,
  Refresh,
  Search,
} from '@mui/icons-material';

import { apiService } from '../services/api';
import { Case } from '../types';

function getStatusColor(status: string) {
  switch (status.toLowerCase()) {
    case 'active':
      return 'warning';
    case 'in_progress':
      return 'info';
    case 'resolved':
      return 'success';
    case 'escalated':
      return 'error';
    default:
      return 'default';
  }
}

function getPriorityColor(priority: string) {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'default';
  }
}

interface CaseDetailDialogProps {
  case: Case | null;
  open: boolean;
  onClose: () => void;
  onStartWorkflow: (caseId: string) => void;
}

function CaseDetailDialog({ case: selectedCase, open, onClose, onStartWorkflow }: CaseDetailDialogProps) {
  if (!selectedCase) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Case Details - {selectedCase.id}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Case Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Case ID
                  </Typography>
                  <Typography variant="body1">{selectedCase.id}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Customer ID
                  </Typography>
                  <Typography variant="body1">{selectedCase.customer_id}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="h6" color="error">
                    ${selectedCase.amount.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Days Overdue
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {selectedCase.days_overdue} days
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status & Priority
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedCase.status}
                    color={getStatusColor(selectedCase.status) as any}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Priority
                  </Typography>
                  <Chip
                    label={selectedCase.priority}
                    color={getPriorityColor(selectedCase.priority) as any}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedCase.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedCase.updated_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={() => onStartWorkflow(selectedCase.id)}
        >
          Start Workflow
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Cases() {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCases();
  }, []);

  const loadCases = async () => {
    try {
      setLoading(true);
      setError(null);
      
      try {
        const casesData = await apiService.getCases();
        setCases(casesData);
      } catch (err) {
        console.error('Cases API failed:', err);
        setError('Failed to load cases');
      }
    } catch (err) {
      setError('Failed to load cases');
      console.error('Cases error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCase = (caseItem: Case) => {
    setSelectedCase(caseItem);
    setDetailDialogOpen(true);
  };

  const handleStartWorkflow = async (caseId: string) => {
    try {
      await apiService.startWorkflow(caseId, 'overdue_processing');
      setDetailDialogOpen(false);
      loadCases(); // Refresh cases
    } catch (err) {
      console.error('Failed to start workflow:', err);
    }
  };

  const filteredCases = cases.filter(
    (caseItem) =>
      caseItem.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      caseItem.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      caseItem.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h4" component="h1" gutterBottom>
            Cases
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage overdue cases and workflows
          </Typography>
        </div>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadCases}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
          >
            New Case
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search cases by ID, customer, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
      </Paper>

      {/* Cases Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Case ID</TableCell>
                <TableCell>Customer ID</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Days Overdue</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Priority</TableCell>
                <TableCell align="center">Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCases
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((caseItem) => (
                  <TableRow key={caseItem.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {caseItem.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{caseItem.customer_id}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium" color="error">
                        ${caseItem.amount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        color={caseItem.days_overdue > 60 ? 'error' : 'warning.main'}
                        fontWeight="medium"
                      >
                        {caseItem.days_overdue}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={caseItem.status}
                        color={getStatusColor(caseItem.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={caseItem.priority}
                        color={getPriorityColor(caseItem.priority) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {new Date(caseItem.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewCase(caseItem)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Start Workflow">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleStartWorkflow(caseItem.id)}
                        >
                          <PlayArrow />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredCases.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Case Detail Dialog */}
      <CaseDetailDialog
        case={selectedCase}
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        onStartWorkflow={handleStartWorkflow}
      />
    </Box>
  );
}